import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export const hrmService = {
  /**
   * Get all blood groups
   */
  async getBloodGroups() {
    try {
      const response = await apiClient.get('/auth/hrm/get-blood-group-list')
      return response.data
    } catch (error) {
      console.error('Error fetching blood groups:', error)
      throw error
    }
  },

  /**
   * Get all gender types
   */
  async getGenderTypes() {
    try {
      const response = await apiClient.get('/auth/hrm/get-gender-list')
      return response.data
    } catch (error) {
      console.error('Error fetching gender types:', error)
      throw error
    }
  },

  /**
   * Get all religion types
   */
  async getReligionTypes() {
    try {
      const response = await apiClient.get('/auth/hrm/get-religion-list')
      return response.data
    } catch (error) {
      console.error('Error fetching religion types:', error)
      throw error
    }
  },

  /**
   * Get all marital status types
   */
  async getMaritalStatusTypes() {
    try {
      const response = await apiClient.get('/auth/hrm/get-marital-status-list')
      return response.data
    } catch (error) {
      console.error('Error fetching marital status types:', error)
      throw error
    }
  },

  /**
   * Get all departments
   */
  async getDepartments() {
    try {
      const response = await apiClient.get('/auth/hrm/get-department-list')
      return response.data
    } catch (error) {
      console.error('Error fetching departments:', error)
      throw error
    }
  },

  /**
   * Get all designations
   */
  async getDesignations() {
    try {
      const response = await apiClient.get('/auth/hrm/get-designation-list')
      return response.data
    } catch (error) {
      console.error('Error fetching designations:', error)
      throw error
    }
  },

  /**
   * Get all employee types
   */
  async getEmployeeTypes() {
    try {
      const response = await apiClient.get('/auth/hrm/get-employee-type-list')
      return response.data
    } catch (error) {
      console.error('Error fetching employee types:', error)
      throw error
    }
  },

  /**
   * Get all offices/branches
   */
  async getOffices() {
    try {
      const response = await apiClient.get('/auth/hrm/get-office-list')
      return response.data
    } catch (error) {
      console.error('Error fetching offices:', error)
      throw error
    }
  },

  /**
   * Get all sections
   */
  async getSections() {
    try {
      const response = await apiClient.get('/auth/hrm/get-section-list')
      return response.data
    } catch (error) {
      console.error('Error fetching sections:', error)
      throw error
    }
  },

  /**
   * Get employee list
   */
  async getEmployees() {
    try {
      const response = await apiClient.get('/auth/hrm/get-employee-list')
      return response.data
    } catch (error) {
      console.error('Error fetching employees:', error)
      throw error
    }
  },

  /**
   * Save or update employee
   */
  async saveEmployee(employeeData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-update-employee', employeeData)
      return response.data
    } catch (error) {
      console.error('Error saving employee:', error)
      throw error
    }
  },

  /**
   * Get educational information list
   */
  async getEducationalInfoList() {
    try {
      const response = await apiClient.post('/auth/hrm/get-educational-info-list')
      return response.data
    } catch (error) {
      console.error('Error fetching educational info list:', error)
      throw error
    }
  },

  /**
   * Get educational information by employee ID
   */
  async getEducationalInfoByEmployee(employeeId) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-educational-info-by-employee/${employeeId}`)
      return response.data
    } catch (error) {
      console.error('Error fetching educational info by employee:', error)
      throw error
    }
  },

  /**
   * Get educational information by ID
   */
  async getEducationalInfoById(id) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-educational-info/${id}`)
      return response.data
    } catch (error) {
      console.error('Error fetching educational info by ID:', error)
      throw error
    }
  },

  /**
   * Save or update educational information
   */
  async saveEducationalInfo(educationalData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-update-educational-info', educationalData)
      return response.data
    } catch (error) {
      console.error('Error saving educational info:', error)
      throw error
    }
  },

  /**
   * Delete educational information
   */
  async deleteEducationalInfo(id) {
    try {
      const response = await apiClient.delete(`/auth/hrm/delete-educational-info/${id}`)
      return response.data
    } catch (error) {
      console.error('Error deleting educational info:', error)
      throw error
    }
  },

  /**
   * Get educational information grouped by level for an employee
   */
  async getEducationalInfoGroupedByEmployee(employeeId) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-educational-info-grouped-by-employee/${employeeId}`)
      return response.data
    } catch (error) {
      console.error('Error fetching educational info grouped by employee:', error)
      throw error
    }
  },

  /**
   * Save multiple educational records for an employee
   */
  async saveEmployeeEducationalInfo(employeeId, educationalData, files = {}) {
    try {
      const formData = new FormData()
      formData.append('employee_id', employeeId)
      
      // Send the educational data as a JSON string
      formData.append('educational_data', JSON.stringify(educationalData))
      
      // Add file uploads if provided
      if (files.certificateFile) {
        formData.append('certificate_file', files.certificateFile)
      }
      if (files.transcriptFile) {
        formData.append('transcript_file', files.transcriptFile)
      }
      
      const response = await apiClient.post('/auth/hrm/save-employee-educational-info', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error saving employee educational info:', error)
      throw error
    }
  },

  /**
   * Update educational information
   */
  async updateEducationalInfo(id, educationalData) {
    try {
      const formData = new FormData()
      Object.keys(educationalData).forEach(key => {
        if (educationalData[key] !== null && educationalData[key] !== undefined) {
          formData.append(key, educationalData[key])
        }
      })
      
      const response = await apiClient.put(`/auth/hrm/update-educational-info/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error updating educational info:', error)
      throw error
    }
  },

  /**
   * Bulk delete educational information
   */
  async bulkDeleteEducationalInfo(ids) {
    try {
      const response = await apiClient.post('/auth/hrm/bulk-delete-educational-info', { ids })
      return response.data
    } catch (error) {
      console.error('Error bulk deleting educational info:', error)
      throw error
    }
  },

  // Address Information APIs
  async getAddressInfoList(params = {}) {
    try {
      const response = await apiClient.post('/auth/hrm/get-address-info-list', params)
      return response.data
    } catch (error) {
      console.error('Error fetching address info list:', error)
      throw error
    }
  },

  async getAddressInfoById(id) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-address-info/${id}`)
      return response.data
    } catch (error) {
      console.error('Error fetching address info:', error)
      throw error
    }
  },

  async getAddressInfoByEmployee(employeeId) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-address-info-by-employee/${employeeId}`)
      return response.data
    } catch (error) {
      console.error('Error fetching address info by employee:', error)
      throw error
    }
  },

  async saveAddressInfo(addressData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-update-address-info', addressData)
      return response.data
    } catch (error) {
      console.error('Error saving address info:', error)
      throw error
    }
  },

  async saveEmployeeAddressInfo(employeeId, addressData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-employee-address-info', {
        employee_id: employeeId,
        address_data: addressData
      })
      return response.data
    } catch (error) {
      console.error('Error saving employee address info:', error)
      throw error
    }
  },

  async updateAddressInfo(id, addressData) {
    try {
      const response = await apiClient.put(`/auth/hrm/update-address-info/${id}`, addressData)
      return response.data
    } catch (error) {
      console.error('Error updating address info:', error)
      throw error
    }
  },

  async deleteAddressInfo(id) {
    try {
      const response = await apiClient.delete(`/auth/hrm/delete-address-info/${id}`)
      return response.data
    } catch (error) {
      console.error('Error deleting address info:', error)
      throw error
    }
  },

  // Previous Employment Information APIs
  async getPreviousEmploymentInfoList(params = {}) {
    try {
      const response = await apiClient.post('/auth/hrm/get-previous-employment-info-list', params)
      return response.data
    } catch (error) {
      console.error('Error fetching previous employment info list:', error)
      throw error
    }
  },

  async getPreviousEmploymentInfoById(id) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-previous-employment-info/${id}`)
      return response.data
    } catch (error) {
      console.error('Error fetching previous employment info:', error)
      throw error
    }
  },

  async getPreviousEmploymentInfoByEmployee(employeeId) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-previous-employment-info-by-employee/${employeeId}`)
      return response.data
    } catch (error) {
      console.error('Error fetching previous employment info by employee:', error)
      throw error
    }
  },

  async savePreviousEmploymentInfo(employmentData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-update-previous-employment-info', employmentData)
      return response.data
    } catch (error) {
      console.error('Error saving previous employment info:', error)
      throw error
    }
  },

  async saveEmployeePreviousEmploymentInfo(employeeId, employmentData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-employee-previous-employment-info', {
        employee_id: employeeId,
        employment_data: employmentData
      })
      return response.data
    } catch (error) {
      console.error('Error saving employee previous employment info:', error)
      throw error
    }
  },

  async updatePreviousEmploymentInfo(id, employmentData) {
    try {
      const response = await apiClient.put(`/auth/hrm/update-previous-employment-info/${id}`, employmentData)
      return response.data
    } catch (error) {
      console.error('Error updating previous employment info:', error)
      throw error
    }
  },

  async deletePreviousEmploymentInfo(id) {
    try {
      const response = await apiClient.delete(`/auth/hrm/delete-previous-employment-info/${id}`)
      return response.data
    } catch (error) {
      console.error('Error deleting previous employment info:', error)
      throw error
    }
  },

  // Other Information APIs
  async getOtherInfoList(params = {}) {
    try {
      const response = await apiClient.post('/auth/hrm/get-other-info-list', params)
      return response.data
    } catch (error) {
      console.error('Error fetching other info list:', error)
      throw error
    }
  },

  async getOtherInfoById(id) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-other-info/${id}`)
      return response.data
    } catch (error) {
      console.error('Error fetching other info:', error)
      throw error
    }
  },

  async getOtherInfoByEmployee(employeeId) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-other-info-by-employee/${employeeId}`)
      return response.data
    } catch (error) {
      console.error('Error fetching other info by employee:', error)
      throw error
    }
  },

  async saveOtherInfo(otherInfoData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-update-other-info', otherInfoData)
      return response.data
    } catch (error) {
      console.error('Error saving other info:', error)
      throw error
    }
  },

  async saveEmployeeOtherInfo(employeeId, otherInfoData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-employee-other-info', {
        employee_id: employeeId,
        other_info_data: otherInfoData
      })
      return response.data
    } catch (error) {
      console.error('Error saving employee other info:', error)
      throw error
    }
  },

  async updateOtherInfo(id, otherInfoData) {
    try {
      const response = await apiClient.put(`/auth/hrm/update-other-info/${id}`, otherInfoData)
      return response.data
    } catch (error) {
      console.error('Error updating other info:', error)
      throw error
    }
  },

  async deleteOtherInfo(id) {
    try {
      const response = await apiClient.delete(`/auth/hrm/delete-other-info/${id}`)
      return response.data
    } catch (error) {
      console.error('Error deleting other info:', error)
      throw error
    }
  },

  async bulkDeleteOtherInfo(ids) {
    try {
      const response = await apiClient.post('/auth/hrm/bulk-delete-other-info', { ids })
      return response.data
    } catch (error) {
      console.error('Error bulk deleting other info:', error)
      throw error
    }
  },

  // Settings Information APIs
  async getSettingsInfoList(params = {}) {
    try {
      const response = await apiClient.post('/auth/hrm/get-settings-info-list', params)
      return response.data
    } catch (error) {
      console.error('Error fetching settings info list:', error)
      throw error
    }
  },

  async getSettingsInfoById(id) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-settings-info/${id}`)
      return response.data
    } catch (error) {
      console.error('Error fetching settings info:', error)
      throw error
    }
  },

  async getSettingsInfoByEmployee(employeeId) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-settings-info-by-employee/${employeeId}`)
      return response.data
    } catch (error) {
      console.error('Error fetching settings info by employee:', error)
      throw error
    }
  },

  async saveSettingsInfo(settingsData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-update-settings-info', settingsData)
      return response.data
    } catch (error) {
      console.error('Error saving settings info:', error)
      throw error
    }
  },

  async saveEmployeeSettingsInfo(employeeId, settingsData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-employee-settings-info', {
        employee_id: employeeId,
        settings_data: settingsData
      })
      return response.data
    } catch (error) {
      console.error('Error saving employee settings info:', error)
      throw error
    }
  },

  async updateSettingsInfo(id, settingsData) {
    try {
      const response = await apiClient.put(`/auth/hrm/update-settings-info/${id}`, settingsData)
      return response.data
    } catch (error) {
      console.error('Error updating settings info:', error)
      throw error
    }
  },

  async deleteSettingsInfo(id) {
    try {
      const response = await apiClient.delete(`/auth/hrm/delete-settings-info/${id}`)
      return response.data
    } catch (error) {
      console.error('Error deleting settings info:', error)
      throw error
    }
  },

  async bulkDeleteSettingsInfo(ids) {
    try {
      const response = await apiClient.post('/auth/hrm/bulk-delete-settings-info', { ids })
      return response.data
    } catch (error) {
      console.error('Error bulk deleting settings info:', error)
      throw error
    }
  },

  async getOTApplicableEmployees() {
    try {
      const response = await apiClient.get('/auth/hrm/get-ot-applicable-employees')
      return response.data
    } catch (error) {
      console.error('Error fetching OT applicable employees:', error)
      throw error
    }
  },

  async calculateOTAmount(employeeId, hours, type = 'hourly') {
    try {
      const response = await apiClient.post('/auth/hrm/calculate-ot-amount', {
        employee_id: employeeId,
        hours: hours,
        type: type
      })
      return response.data
    } catch (error) {
      console.error('Error calculating OT amount:', error)
      throw error
    }
  },

  // ==================== INCREMENT MANAGEMENT ====================

  /**
   * Get all increments with filters
   */
  async getIncrementList(params = {}) {
    try {
      const response = await apiClient.get('/auth/hrm/get-increment-list', { params })
      return response.data
    } catch (error) {
      console.error('Error fetching increment list:', error)
      throw error
    }
  },

  /**
   * Get increment by ID
   */
  async getIncrementById(id) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-increment-by-id/${id}`)
      return response.data
    } catch (error) {
      console.error('Error fetching increment by ID:', error)
      throw error
    }
  },

  /**
   * Get increments by employee ID
   */
  async getIncrementsByEmployee(employeeId) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-increments-by-employee/${employeeId}`)
      return response.data
    } catch (error) {
      console.error('Error fetching employee increments:', error)
      throw error
    }
  },

  /**
   * Get employee details by ID
   */
  async getEmployeeDetails(employeeId) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-employee-details/${employeeId}`)
      return response.data
    } catch (error) {
      console.error('Error fetching employee details:', error)
      throw error
    }
  },

  /**
   * Get employee's current salary information
   */
  async getEmployeeSalaryInfo(employeeId) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-salary-info-by-employee/${employeeId}`)
      return response.data
    } catch (error) {
      console.error('Error fetching employee salary info:', error)
      throw error
    }
  },

  /**
   * Save or update increment
   */
  async saveOrUpdateIncrement(incrementData) {
    try {
      const response = await apiClient.post('/auth/hrm/save-update-increment', incrementData)
      return response.data
    } catch (error) {
      console.error('Error saving increment:', error)
      throw error
    }
  },

  /**
   * Update existing increment
   */
  async updateIncrement(id, incrementData) {
    try {
      const response = await apiClient.put(`/auth/hrm/update-increment/${id}`, incrementData)
      return response.data
    } catch (error) {
      console.error('Error updating increment:', error)
      throw error
    }
  },

  /**
   * Delete increment
   */
  async deleteIncrement(id) {
    try {
      const response = await apiClient.delete(`/auth/hrm/delete-increment/${id}`)
      return response.data
    } catch (error) {
      console.error('Error deleting increment:', error)
      throw error
    }
  },

  /**
   * Approve increment
   */
  async approveIncrement(id, remarks = null) {
    try {
      const response = await apiClient.post(`/auth/hrm/approve-increment/${id}`, { remarks })
      return response.data
    } catch (error) {
      console.error('Error approving increment:', error)
      throw error
    }
  },

  /**
   * Reject increment
   */
  async rejectIncrement(id, remarks = null) {
    try {
      const response = await apiClient.post(`/auth/hrm/reject-increment/${id}`, { remarks })
      return response.data
    } catch (error) {
      console.error('Error rejecting increment:', error)
      throw error
    }
  },

  /**
   * Get increment statistics
   */
  async getIncrementStatistics(params = {}) {
    try {
      const response = await apiClient.get('/auth/hrm/get-increment-statistics', { params })
      return response.data
    } catch (error) {
      console.error('Error fetching increment statistics:', error)
      throw error
    }
  },

  /**
   * Generate reference number
   */
  async generateIncrementReferenceNumber() {
    try {
      const response = await apiClient.get('/auth/hrm/generate-increment-ref-no')
      return response.data
    } catch (error) {
      console.error('Error generating reference number:', error)
      throw error
    }
  },

  /**
   * Calculate salary components
   */
  async calculateSalaryComponents(basicSalary, packageType = null) {
    try {
      const response = await apiClient.post('/auth/hrm/calculate-salary-components', {
        basic_salary: basicSalary,
        package: packageType
      })
      return response.data
    } catch (error) {
      console.error('Error calculating salary components:', error)
      throw error
    }
  },

  // ==================== PROMOTION METHODS ====================

  /**
   * Get promotion list
   */
  async getPromotionList(params = {}) {
    try {
      const response = await apiClient.get('/auth/hrm/get-promotion-list', { params })
      return response.data
    } catch (error) {
      console.error('Error fetching promotion list:', error)
      throw error
    }
  },

  /**
   * Get promotion by ID
   */
  async getPromotionById(id) {
    try {
      const response = await apiClient.get(`/auth/hrm/get-promotion/${id}`)
      return response.data
    } catch (error) {
      console.error('Error fetching promotion:', error)
      throw error
    }
  },

  /**
   * Save or update promotion
   */
  async saveOrUpdatePromotion(promotionData) {
    try {
      // Ensure all required fields are present
      const completePromotionData = this.validateAndCompletePromotionData(promotionData)
      
      const response = await apiClient.post('/auth/hrm/save-or-update-promotion', completePromotionData)
      return response.data
    } catch (error) {
      console.error('Error saving promotion:', error)
      throw error
    }
  },

  /**
   * Validate and complete promotion data with required fields
   */
  validateAndCompletePromotionData(promotionData) {
    // Required fields that must be present
    const requiredFields = [
      'employee_id',
      'ref_no', 
      'promotion_type',
      'promotion_date',
      'effect_date',
      'current_designation_id',
      'new_designation_id', 
      'current_department_id',
      'new_department_id',
      'current_basic_salary',
      'new_basic_salary',
      'salary_increment_amount',
      'after_salary',
      'after_deduction'
    ]

    // Check for missing required fields
    const missingFields = requiredFields.filter(field => {
      if (field === 'after_salary' || field === 'after_deduction') {
        return !promotionData[field] || typeof promotionData[field] !== 'object'
      }
      return !promotionData[field]
    })

    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`)
    }

    // Ensure numeric fields are numbers
    const numericFields = ['current_basic_salary', 'new_basic_salary', 'salary_increment_amount']
    numericFields.forEach(field => {
      if (typeof promotionData[field] === 'string') {
        promotionData[field] = parseFloat(promotionData[field])
      }
    })

    // Ensure dates are in correct format
    if (promotionData.promotion_date && !promotionData.promotion_date.includes('-')) {
      // Convert from DD/MM/YYYY to YYYY-MM-DD if needed
      const date = new Date(promotionData.promotion_date)
      promotionData.promotion_date = date.toISOString().split('T')[0]
    }
    
    if (promotionData.effect_date && !promotionData.effect_date.includes('-')) {
      // Convert from DD/MM/YYYY to YYYY-MM-DD if needed
      const date = new Date(promotionData.effect_date)
      promotionData.effect_date = date.toISOString().split('T')[0]
    }

    // Set default values for optional fields
    const completeData = {
      ...promotionData,
      status: promotionData.status || 'active',
      approval_status: promotionData.approval_status || 'pending',
      current_grade: promotionData.current_grade || '',
      new_grade: promotionData.new_grade || '',
      remarks: promotionData.remarks || '',
      after_package: promotionData.after_package || ''
    }

    return completeData
  },

  /**
   * Create complete promotion data structure
   */
  createPromotionData(employeeId, promotionType = 'merit') {
    const currentDate = new Date()
    const effectDate = new Date()
    effectDate.setDate(currentDate.getDate() + 10) // 10 days from now

    return {
      employee_id: employeeId,
      ref_no: `PROM${Date.now()}`, // Generate unique reference
      promotion_type: promotionType,
      promotion_date: currentDate.toISOString().split('T')[0], // YYYY-MM-DD
      effect_date: effectDate.toISOString().split('T')[0], // YYYY-MM-DD
      current_designation_id: null, // Must be set by frontend
      new_designation_id: null, // Must be set by frontend
      current_department_id: null, // Must be set by frontend
      new_department_id: null, // Must be set by frontend
      current_grade: '',
      new_grade: '',
      current_basic_salary: 0, // Will be auto-loaded from current salary
      new_basic_salary: 0, // Must be set by frontend
      salary_increment_amount: 0, // Must be set by frontend
      status: 'active',
      approval_status: 'pending',
      remarks: '',
      after_package: '',
      after_salary: {
        gross_salary: 0,
        basic: 0,
        house_rent: 0,
        medical: 0,
        education: 0,
        mobile: 0,
        transport: 0,
        technical: 0,
        festival: 0,
        tiffin: 0,
        wash: 0,
        incentive: 0,
        others: 0
      },
      after_deduction: {
        tax_deduct: 0,
        pf: 0,
        car_use: 0,
        gas_bill: 0,
        electricity_bill: 0,
        telephone_bill: 0,
        insurance: 0,
        others_deduct: 0
      }
    }
  },

  /**
   * Update promotion
   */
  async updatePromotion(id, promotionData) {
    try {
      const response = await apiClient.put(`/auth/hrm/update-promotion/${id}`, promotionData)
      return response.data
    } catch (error) {
      console.error('Error updating promotion:', error)
      throw error
    }
  },

  /**
   * Delete promotion
   */
  async deletePromotion(id) {
    try {
      const response = await apiClient.delete(`/auth/hrm/delete-promotion/${id}`)
      return response.data
    } catch (error) {
      console.error('Error deleting promotion:', error)
      throw error
    }
  },

  /**
   * Generate promotion reference number
   */
  async generatePromotionReferenceNumber() {
    try {
      const response = await apiClient.get('/auth/hrm/generate-promotion-ref-no')
      return response.data
    } catch (error) {
      console.error('Error generating promotion reference number:', error)
      throw error
    }
  },

  // ==================== REPORT METHODS ====================

  /**
   * Get promotion report
   */
  async getPromotionReport(params = {}) {
    try {
      const response = await apiClient.get('/auth/hrm/promotion-report', { params })
      return response.data
    } catch (error) {
      console.error('Error fetching promotion report:', error)
      throw error
    }
  },

  /**
   * Get nominee report
   */
  async getNomineeReport(params = {}) {
    try {
      const response = await apiClient.get('/auth/hrm/nominee-report', { params })
      return response.data
    } catch (error) {
      console.error('Error fetching nominee report:', error)
      throw error
    }
  },

  /**
   * Get employee summary report
   */
  async getEmployeeSummaryReport(params = {}) {
    try {
      const response = await apiClient.get('/auth/hrm/employee-summary-report', { params })
      return response.data
    } catch (error) {
      console.error('Error fetching employee summary report:', error)
      throw error
    }
  },

  // ==================== DROPDOWN METHODS ====================

  /**
   * Get department list
   */
  async getDepartmentList() {
    try {
      const response = await apiClient.get('/auth/hrm/get-department-list')
      return response.data
    } catch (error) {
      console.error('Error fetching department list:', error)
      throw error
    }
  },

  /**
   * Get designation list
   */
  async getDesignationList() {
    try {
      const response = await apiClient.get('/auth/hrm/get-designation-list')
      return response.data
    } catch (error) {
      console.error('Error fetching designation list:', error)
      throw error
    }
  },

  /**
   * Get employee type list
   */
  async getEmployeeTypeList() {
    try {
      const response = await apiClient.get('/auth/hrm/get-employee-type-list')
      return response.data
    } catch (error) {
      console.error('Error fetching employee type list:', error)
      throw error
    }
  },

  /**
   * Get branch list
   */
  async getBranchList() {
    try {
      const response = await apiClient.post('/auth/get-branch-list', {})
      return response.data
    } catch (error) {
      console.error('Error fetching branch list:', error)
      // Return empty array as fallback
      return { data: [] }
    }
  },

  // Expose apiClient for direct use
  apiClient
}

export default hrmService
